import { useQueryClient } from "@tanstack/react-query";
import { useContext, useEffect, useRef } from "react";
import { useLocation, useNavigate, useParams } from "react-router";

import { routes } from "~/app/routes/web-routes.ts";
import { ShipmentTenderedRepModal } from "~/features/ltl-tender/components/shipment-tendered-rep-modal/shipment-tendered-rep-modal.tsx";
import {
	useSeedActiveCustomer,
	useTenderModals,
} from "~/features/ltl-tender/hooks/use-tender-modals.ts";
import styles from "~/features/ltl-tender/index.module.css";
import { getLtlStepFromRoute } from "~/features/ltl-tender/utils/get-ltl-step-from-route.ts";
import { ConfirmationModal } from "~/features/quote/components/shipment-details/components/confirmation-modal/confirmation-modal.tsx";
import { TenderModal } from "~/features/quote/components/shipment-details/components/tender-modal/tender-modal.tsx";
import { useQuoteBoardReturn } from "~/features/quote-board/hooks/use-quote-board-return.ts";
import { TraxModal } from "~/shared/components/trax-modal/trax-modal.tsx";
import { TraxPageHeader } from "~/shared/components/trax-page-header/trax-page-header.tsx";
import { useDeploymentType } from "~/shared/hooks/use-deployment-type.ts";

import { CarrierCard } from "../quote/components/carrier-selection/components/carrier-card/carrier-card.tsx";
import { CarrierSelectionForm } from "../quote/components/carrier-selection/index.tsx";
import {
	CompletedHeader,
	CompletedHeaderType,
} from "../quote/components/completed-header/completed-header.tsx";
import { QuoteDetailsSummary } from "../quote/components/quote-details/components/quote-details-summary/quote-details-summary.tsx";
import type { Step } from "../quote/components/quoting-accordion/quoting-accordion.tsx";
import { QuotingAccordion } from "../quote/components/quoting-accordion/quoting-accordion.tsx";
import {
	QuoteDetailsContext,
	QuoteDetailsProvider,
} from "../quote/context/quote-details-context.tsx";
import { useCarrierPageLoader } from "../quote/hooks/use-carrier-selection-hooks.ts";
import { useSeededRerate } from "./hooks/use-post-rerate.ts";

export const LtlShipmentContent = () => {
	const queryClient = useQueryClient();
	const { quoteState, loadQuoteFromResponse } = useContext(QuoteDetailsContext);
	const { pageData } = useCarrierPageLoader({ skipQuoteFetch: true });
	const navigate = useNavigate();
	const { quoteId } = useParams<{ quoteId: string }>();
	const location = useLocation();
	const currentStep = getLtlStepFromRoute(location.pathname);
	const { returnToQuoteBoard } = useQuoteBoardReturn();
	const { isInternal } = useDeploymentType();

	const {
		showModal,
		errorMessage,
		handleShowConfirmationModal,
		handleShowErrorModal,
		handleShowTenderModal,
		handleShowAccountRepModal,
	} = useTenderModals(routes.ltlShipment);

	const quoteIdNumber = Number(quoteId) || undefined;

	// The rerate response is seeded into the query cache by the "Rerate" action on
	// the Shipment Management page — the only legitimate way to reach this page.
	const { rerateResponse } = useSeededRerate(quoteIdNumber);

	// Capture, on first mount, whether we arrived with a seeded rerate. A browser
	// refresh or a manually typed URL starts with an empty cache, so there is no
	// seeded response. We must send the user back rather than let `useQuoteLoader`
	// (via `useCarrierPageLoader`) silently re-fetch and resume the underlying
	// quote, which would otherwise make the page appear to work after a refresh.
	const arrivedWithRerate = useRef(rerateResponse !== undefined);

	useEffect(() => {
		if (arrivedWithRerate.current) {
			if (rerateResponse) {
				loadQuoteFromResponse(rerateResponse);
			}
			return;
		}
		// `idx` is React Router's position in the history stack. A value > 0 means
		// there is a prior in-app entry to return to (e.g. on a refresh). When it is
		// missing/0 (a typed URL in a fresh tab) fall back to the home page.
		const historyIndex =
			(globalThis.history.state as { idx?: number } | null)?.idx ?? 0;
		if (historyIndex > 0) {
			navigate(-1);
		} else {
			navigate(routes.root, { replace: true });
		}
	}, [rerateResponse, loadQuoteFromResponse, navigate]);

	useSeedActiveCustomer();

	const handleTenderModalClose = () => {
		if (returnToQuoteBoard()) return;
		if (quoteState?.poNumber) {
			// Drop the cached shipment so the destination page shows its loading
			// spinner (via `isLoading`) while it refetches the rerated data, rather
			// than briefly flashing the pre-rerate values.
			queryClient.removeQueries({ queryKey: ["shipment"] });
			navigate(
				routes.shipmentManagement.replace(
					":poNumber",
					quoteState.poNumber.toString(),
				),
			);
		} else {
			handleShowTenderModal(false);
		}
	};

	// Render nothing while redirecting away (refresh / direct URL) so the page
	// never flashes resumed content from the underlying quote fetch.
	if (!arrivedWithRerate.current) {
		return null;
	}

	const steps: Step[] = [
		{
			step: 1,
			id: "shipment-details",
			title: "Shipment Details",
			active: currentStep === 1,
			completed: currentStep > 1,
			completedHeader: (
				<CompletedHeader
					canRevise={false}
					onClick={() => {}}
					quoteState={quoteState}
					type={CompletedHeaderType.QuoteDetails}
				/>
			),
			completedContent: (
				<div style={{ padding: "1rem" }}>
					<QuoteDetailsSummary />
					{quoteState?.quotePreviousRate ? (
						<div style={{ padding: "1rem" }}>
							<h1>Previously Selected Carrier</h1>
							<CarrierCard
								carrierPrice={quoteState?.quotePreviousRate}
								isReadOnly={false}
								isReratePrevCarrier={true}
								onStepChange={() => undefined}
								pageData={pageData}
							/>
						</div>
					) : null}
				</div>
			),
			content: null,
		},
		{
			step: 2,
			id: "carrier-selection",
			title: "Carrier Selection",
			active: currentStep === 2,
			completed: false,
			content: (
				<CarrierSelectionForm
					enableShipmentValueModal={true}
					enableTenderOnBook={true}
					isRerate={true}
					onTenderError={(message) => handleShowErrorModal(true, message)}
					onTenderSuccess={() => handleShowTenderModal(true)}
				/>
			),
		},
	];

	return (
		<>
			{showModal.confirmation ? (
				<ConfirmationModal
					onToggleModal={() => handleShowConfirmationModal(false)}
					showModal={showModal.confirmation}
				/>
			) : null}
			{showModal.tender ? (
				<TenderModal
					onToggleModal={handleTenderModalClose}
					showModal={showModal.tender}
					title={"Shipment Rerated & Tendered"}
				/>
			) : null}
			{showModal.accountRep ? (
				<ShipmentTenderedRepModal
					onClose={() => handleShowAccountRepModal(false)}
				/>
			) : null}
			{showModal.error ? (
				<TraxModal
					onClose={() => handleShowErrorModal(false)}
					title={"Tender Failed"}
					type={"error"}
					width={500}
				>
					<p>{errorMessage}</p>
				</TraxModal>
			) : null}
			<TraxPageHeader
				representativeType={"ltl"}
				showSupportMessage={!isInternal}
				title={"Re-Rate Shipment"}
			/>
			<QuotingAccordion steps={steps} />
		</>
	);
};

export const RerateShipment = () => {
	return (
		<div className={styles.ltlShipmentWrapper}>
			<QuoteDetailsProvider>
				<LtlShipmentContent />
			</QuoteDetailsProvider>
		</div>
	);
};
